import { gameEvents } from '../game/events';
import { useEffect } from 'preact/hooks';

type InputEventName = 'input:left' | 'input:right' | 'input:jump' | 'input:crouch' | 'input:ability' | 'input:roll';

function ControlButton({ event, label, className = '' }: { event: InputEventName; label: string; className?: string }) {
  const set = (active: boolean) => gameEvents.emit(event, { active });
  const release = () => set(false);
  return (
    <button
      class={`touch-button ${className}`}
      type="button"
      aria-label={label}
      onPointerDown={(pointerEvent) => {
        pointerEvent.preventDefault();
        try {
          pointerEvent.currentTarget.setPointerCapture(pointerEvent.pointerId);
        } catch {
          // Some mobile browsers may reject capture during interrupted gestures.
        }
        set(true);
      }}
      onPointerUp={release}
      onPointerCancel={release}
      onContextMenu={(event) => event.preventDefault()}
    >
      {label}
    </button>
  );
}

export function TouchControls({
  leftHanded,
  canCrouch,
  jumpButtonScale,
  actionButtonScale,
}: {
  leftHanded: boolean;
  canCrouch: boolean;
  jumpButtonScale: number;
  actionButtonScale: number;
}) {
  useEffect(() => {
    const reset = () => gameEvents.emit('input:reset', undefined);
    const resetWhenHidden = () => {
      if (document.visibilityState !== 'visible') reset();
    };
    document.addEventListener('visibilitychange', resetWhenHidden);
    return () => {
      reset();
      document.removeEventListener('visibilitychange', resetWhenHidden);
    };
  }, []);

  return (
    <div
      class={`touch-controls${leftHanded ? ' touch-controls--left-handed' : ''}${canCrouch ? ' touch-controls--crouch' : ''}`}
      aria-label="Touch game controls"
      style={{
        '--jump-button-scale': jumpButtonScale,
        '--action-button-scale': actionButtonScale,
      }}
    >
      <div class="touch-controls__move">
        <ControlButton event="input:left" label="LEFT" className="touch-button--move touch-button--left" />
        <ControlButton event="input:right" label="RIGHT" className="touch-button--move touch-button--right" />
      </div>
      <div class="touch-controls__actions">
        {canCrouch && <ControlButton event="input:crouch" label="DUCK" className="touch-button--small touch-button--duck" />}
        <ControlButton event="input:roll" label="ROLL" className="touch-button--small touch-button--roll" />
        <ControlButton event="input:ability" label="ABILITY" className="touch-button--ability" />
        <ControlButton event="input:jump" label="JUMP" className="touch-button--primary touch-button--jump" />
      </div>
    </div>
  );
}
